import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import MonetizationWaitlistDialog from "@/components/MonetizationWaitlistDialog";

const FEATURES = [
  {
    key: "rgm_pro",
    icon: "👑",
    title: "RGM Pro",
    pitch: "Deeper analytics, advanced customization, priority support and early-access features for serious artists.",
    bullets: ["Track-level analytics & demographics", "Custom profile themes", "Priority A&R review queue", "Early-access tools and beta features"],
  },
  {
    key: "promote_track",
    icon: "🚀",
    title: "Promote Track",
    pitch: "Pay to push your track into Discover, Charts and the weekly digest. Smart targeting by genre and crew.",
    bullets: ["Boost into Discover & Charts", "Targeted by genre / location / crew", "Featured in weekly digest emails", "Real-time impression dashboard"],
  },
  {
    key: "featured_placement",
    icon: "✨",
    title: "Featured Placement",
    pitch: "Reserve a homepage or Magazine cover slot — A&R-curated, high-visibility moments for big releases.",
    bullets: ["Homepage hero slot", "Magazine cover feature", "Curated by RGM A&R", "Limited weekly availability"],
  },
];

const MonetizationPage = () => {
  const [user, setUser] = useState(null);
  const [openFeature, setOpenFeature] = useState(null);

  useEffect(() => { api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(null)); }, []);

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="monetization-page">
      <SharedNavigation currentUser={user} activePage="business" />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block text-xs px-3 py-1 rounded-full bg-purple-500/20 text-purple-200 border border-purple-500/30 mb-3">Coming soon</span>
          <h1 className="text-3xl sm:text-5xl font-bold text-white">Grow faster on RGM Studio</h1>
          <p className="text-gray-400 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
            Three new ways to amplify your music and your business. Join a waitlist to be first in when these launch — no payments processed yet.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <Card
              key={f.key}
              className="p-5 bg-gradient-to-br from-gray-900/80 to-black border-purple-500/30 flex flex-col"
              data-testid={`monetization-card-${f.key}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{f.icon}</span>
                <h2 className="text-xl font-bold text-white">{f.title}</h2>
              </div>
              <p className="text-sm text-gray-300 mb-3">{f.pitch}</p>
              <ul className="text-sm text-gray-400 space-y-1 mb-4 flex-1">
                {f.bullets.map((b, i) => <li key={i} className="flex gap-2"><span className="text-purple-400">•</span>{b}</li>)}
              </ul>
              <Button
                onClick={() => setOpenFeature(f.key)}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                data-testid={`monetization-waitlist-${f.key}`}
              >
                Join waitlist
              </Button>
            </Card>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/dashboard">
            <Button variant="outline" className="text-white border-gray-700">← Back to dashboard</Button>
          </Link>
        </div>
      </div>

      <MonetizationWaitlistDialog
        open={!!openFeature}
        onClose={() => setOpenFeature(null)}
        feature={openFeature}
        defaultEmail={user?.email || ""}
      />
    </div>
  );
};

export default MonetizationPage;
